import { NarrativeItem, NarrativeStore } from '@assets/model/NarrativeStore';

import { useStoreUI } from '@assets/model/Store';
import { observer } from 'mobx-react';
import { ModalEditNarrative } from '../ModalEditNarrative/ModalEditNarrative';
import { Flex } from '../UI/Flex/Flex';
import * as style from './NarrativeElement.module.less';
import { formatDivMarkup } from '@assets/utils/CompUtils';
import * as icons from './icons/*.svg';
import classNames from 'classnames';
import { Fragment } from 'react';

interface NarrativeElementProps {
    item: NarrativeItem;
    narStore: NarrativeStore;
}

const NarrativeElement = observer((props: NarrativeElementProps) => {
    const { item: selectedInfo, narStore } = props;
    const factsTable = narStore?.factsTable || null;

    const onOpenEditItem = (itemId: string, narrative: NarrativeItem) => {
        const ui = useStoreUI();
        ui.modal = {
            title: 'Edit Narrative',
            el: (
                <ModalEditNarrative
                    hypothesis={narrative.hypothesis}
                    ui_display={narrative.ui_display}
                    onEdit={(hypothesis: string, ui_display: string) => {
                        onEditItemInDb(itemId, hypothesis, ui_display);
                        ui.modal = null;
                    }}
                    onDelete={() => {
                        onDeleteItemInDb(narrative);

                        ui.sideChatSelectedContext = null;
                        ui.modal = null;
                    }}
                />
            )
        };
    };

    const onDeleteItemInDb = (narrative: NarrativeItem) => {
        narStore.removeItemFromParent(narrative);
    };

    const onEditItemInDb = (
        itemId: string,
        hypotehsis: string,
        ui_display
    ): void => {
        narStore.editNarrativeItem(itemId, hypotehsis, ui_display);
    };

    const onDeleteFact = (itemId: string, factIndex: number) => {
        if (confirm('Are you sure?')) {
            narStore.hideSupportingFact(itemId, factIndex);
        }
    };

    const render_item_facts = (
        indentLevel: number,
        renderItem: NarrativeItem
    ): JSX.Element => {
        let facts =
            narStore.factsTable[renderItem.itemId]?.supporting_facts ?? [];

        //facts = facts.filter(f => !f.hidden);

        const dispId = renderItem.ui_displayIdGenerated;
        if (facts.length == 0) {
            return null;
        }

        const formattedFacts = facts.map((f, i) => {
            if (f.hidden) {
                return null;
            }
            let sourceModuleStr = '';
            if (f.fact_citation_source_file) {
                sourceModuleStr =
                    ' ' +
                    f.fact_citation_source_file
                        .replace('v_', '')
                        .split('_')
                        .map(
                            word => word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(' ');
            }
            return (
                <Flex
                    key={dispId + '_fact_' + i.toString()}
                    data-indent={indentLevel}
                    className={style.entryContainer}
                    style={{ marginLeft: indentLevel * 20 }}
                >
                    <div className={style.itemIdTitle}>
                        {dispId}
                        {i + 1}
                    </div>
                    <div className={style.blackLineSeperator} />
                    <div
                        style={{
                            flexGrow: 1,
                            overflow: 'auto',
                            marginRight: '10px'
                        }}
                    >
                        {formatDivMarkup(f.exact_fact_quote)}
                        <div className={style.source_module}>
                            Source:
                            {sourceModuleStr}
                        </div>
                    </div>
                    <div className={style.actionIcons}>
                        <img
                            src={icons['trash']}
                            className={style.svgActionIcon}
                            onClick={() => {
                                onDeleteFact(renderItem.itemId, i);
                            }}
                        />
                    </div>
                </Flex>
            );
        });

        return <>{formattedFacts.filter(e => e != null)}</>;
    };
    const render_item_with_indent = (
        indentLevel: number,
        renderItem: NarrativeItem
    ): JSX.Element => {
        const dispId = renderItem.ui_displayIdGenerated;
        const itemLevel = renderItem.level;
        return (
            <Fragment key={renderItem.itemId + '_fragment'}>
                <Flex
                    key={renderItem.itemId}
                    data-indent={indentLevel}
                    className={style.entryContainer}
                    style={{ marginLeft: indentLevel * 20 }}
                    id={'narrative_anchor_' + renderItem.itemId}
                >
                    <div className={style.itemIdTitle}>{dispId}</div>
                    <div className={style.blackLineSeperator} />
                    <div className={style.itemIdText} style={{ flexGrow: 1 }}>
                        {renderItem.hypothesis}
                    </div>
                    <div className={style.actionIcons}>
                        <img
                            src={icons['open_chat']}
                            className={style.svgActionIcon}
                            onClick={() => {
                                const ui = useStoreUI();
                                ui.sideChatSelectedContext = renderItem.itemId;
                            }}
                        />
                        <img
                            src={icons['edit_item']}
                            className={style.svgActionIcon}
                            onClick={() => {
                                onOpenEditItem(renderItem.itemId, renderItem);
                            }}
                        />
                    </div>
                </Flex>
                {renderItem.sub_items.map(e =>
                    render_item_with_indent(indentLevel + 1, e)
                )}
                {itemLevel == 'L3' &&
                    render_item_facts(indentLevel + 1, renderItem)}
            </Fragment>
        );
    };

    return (
        <div
            style={{ paddingBottom: '10px' }}
            id={'narrative_anchor_' + selectedInfo.itemId.replaceAll(':', '_')}
        >
            <div
                style={{
                    padding: '16px',
                    paddingTop: '0px'
                }}
            >
                <div style={{ overflowX: 'auto' }}>
                    <Flex direction="row" wrap="wrap">
                        {render_item_with_indent(0, selectedInfo)}
                    </Flex>
                </div>
            </div>
        </div>
    );
});

export default NarrativeElement;
